import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import type { RoutePath } from "@-/com.npmjs.expo-router";
import {
  Redirect,
  Slot,
  Stack,
  useLayoutSearchParams,
  usePathname,
  useSegments,
} from "@-/com.npmjs.expo-router";
import { ChromeReadyMarker } from "@-/dev.improve.haniverse.components.ui.chrome-ready-marker";
import {
  SpaceSkySurfaceContext,
  SpaceThemeScope,
} from "@-/dev.improve.haniverse.components.ui.space-backdrop";
import { Platform, StyleSheet, View } from "@-/com.npmjs.react-native";
import { StatusBar } from "@-/com.npmjs.expo-status-bar";
import { useUniwind } from "@-/com.npmjs.uniwind";
import {
  Component,
  type ComponentProps,
  type PropsWithChildren,
  type ReactNode,
  useEffect,
  useState,
} from "react";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import {
  NavContextProvider,
  PRIMARY_NAVIGATION_RAIL_WIDTH,
  useIsPrimaryControlsStripFadeHidden,
  useIsPrimaryControlsStripHidden,
} from "@-/dev.improve.haniverse.routes.nav._context";
import {
  NavAccountDrawer,
  NavAccountDrawerTrigger,
  useNavAccountDrawer,
} from "@-/dev.improve.haniverse.routes.nav._components.account-drawer";
import {
  PrimaryControlsStripLeadingHost,
  usePrimaryControlsStripHasBackControl,
} from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.back";
import { useIsOnboardingIntro, useIsOnboardingTour } from "@-/dev.improve.haniverse.lib.onboarding";
import { preloadOnboardingGlobeCanvas } from "@-/dev.improve.haniverse.routes.nav.app.onboarding";
import {
  PrimaryControlsStrip,
  WebBrowseSidebarLayout,
  WebPrimaryControlsStrip,
} from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip";
import {
  IS_SYNC_STATUS_SENTINEL_PUBLISHED,
  SyncStatusSentinel,
} from "@-/dev.improve.haniverse.components.bundle-stamp";
import { AuthSheetProvider } from "@-/dev.improve.haniverse.routes.nav.app.auth.sheet";
import { FrostedBackdropProvider } from "@-/dev.improve.haniverse.components.ui.frosted-surface";
import {
  WelcomeGalaxyEntryHost,
  WelcomeGalaxyExitCapture,
} from "@-/dev.improve.haniverse.routes.nav.app.auth";
import { WebActorOpenDiagnostics } from "#web-actor-open-diagnostics";
import { PrimaryControlsStripBackProvider } from "@-/dev.improve.haniverse.contexts.primary-controls-strip.back";
import { PrimaryControlsStripExpansionProvider } from "@-/dev.improve.haniverse.contexts.primary-controls-strip.expansion";
import { PrimaryControlsStripTitleProvider } from "@-/dev.improve.haniverse.contexts.primary-controls-strip.title";
import {
  PrimaryControlsStripOpaqueChromeProvider,
  useHasPrimaryControlsStripOpaqueChrome,
} from "@-/dev.improve.haniverse.contexts.primary-controls-strip.opaque-chrome";
import { PrimaryControlsStripToolbarHeightProvider } from "@-/dev.improve.haniverse.contexts.primary-controls-strip.toolbar-height";
import {
  ToolbarSheetShadow,
  ToolbarSheetShadowProvider,
  useHasPrimaryControlsStripToolbar,
  usePrimaryControlsStripToolbarFloating,
  usePrimaryControlsStripToolbarHeight,
  usePrimaryControlsStripToolbarSurface,
} from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.toolbar";
import { useSafeAreaInsets } from "@-/com.npmjs.react-native-safe-area-context";
import { PrimaryControlsStripFade } from "#primary-controls-strip-fade";
import { NavEdgeGestures } from "#nav-edge-gestures";
import { LandscapeSafeAreaChrome } from "#landscape-safe-area-chrome";
import { NavHanCoinDepositProvider } from "#nav-han-coin-deposit-provider";
import { HanCoinDepositOverlay } from "@-/dev.improve.haniverse.components.ui.han-coin-deposit";
import { useUsesSidebarLayout } from "#use-uses-sidebar-layout";
import {
  ToolbarDrawerButtonHiddenContext,
  ToolbarLeadingSlotAtLeftContext,
  ToolbarLeadingSlotHiddenContext,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { PrimaryNavigationStripFloatingTabsProvider } from "@-/dev.improve.haniverse.lib.primary-navigation-strip";

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
});

function useNoopNavDevtools() {}

/** Keep the root navigator alive when a provider's authenticated request is rejected. */
class SessionExpiryBoundary extends Component<PropsWithChildren, { expired: boolean }> {
  state = { expired: false };

  static getDerivedStateFromError(error: unknown): { expired: boolean } {
    const seen = new Set<unknown>();
    let cause = error;
    // Database-open errors wrap the original RPC error in `cause`.
    while (cause instanceof Error && !seen.has(cause)) {
      seen.add(cause);
      if ("code" in cause && cause.code === "UNAUTHORIZED") {
        return { expired: true };
      }
      cause = cause.cause;
    }
    throw error;
  }

  render(): ReactNode {
    return this.state.expired ? (
      <Redirect href={{ params: { reason: "session-expired" }, pathname: "/(nav)/auth" }} />
    ) : (
      this.props.children
    );
  }
}

function NavStatusBar() {
  const { theme } = useUniwind();
  return <StatusBar style={theme === "dark" ? "light" : "dark"} />;
}

export function NavLayout({
  base,
  children,
  useDevtools,
}: PropsWithChildren<{
  base: string;
  /** Optional Rozenite (or other) hook that must run under `NavContextProvider`. */
  useDevtools?: () => void;
}>) {
  const { reason } = useLayoutSearchParams<{ reason?: string }>();
  const pathname = usePathname();
  // Recovery must not open the database that just rejected authentication. The auth form's
  // getters stay lazy; signing in bootstraps/reopens it before app queries resume.
  if (reason === "session-expired" && pathname.startsWith("/auth")) {
    return (
      <>
        <ChromeReadyMarker />
        <Stack screenOptions={{ animation: "none", headerShown: false }} />
      </>
    );
  }
  return (
    <SessionExpiryBoundary>
      <NavContextProvider>
        {/* Experimental: retints the off-white chrome for the whole shell, drawer and sheets alike. */}
        <SpaceThemeScope>
          <NavStatusBar />
          {children}
          {/* Above every signed-out entry point (the welcome deck, the account drawer, Settings,
            the Learn first-level prompt) so they all open the same account sheets. */}
          <FrostedBackdropProvider>
            <AuthSheetProvider>
              <PrimaryControlsStripBackProvider>
                <PrimaryControlsStripTitleProvider>
                  <PrimaryControlsStripExpansionProvider>
                    <PrimaryControlsStripOpaqueChromeProvider>
                      <PrimaryControlsStripToolbarHeightProvider>
                        <ToolbarSheetShadowProvider>
                          {/* Rewards earned on any screen deposit their coins into the drawer button. */}
                          <NavHanCoinDepositProvider>
                            <LandscapeSafeAreaChrome
                              overlay={
                                // Signing in from the welcome galaxy zooms into it and turns it into the
                                // Learn card while the app fades in. The entry outlives the welcome
                                // route, so it plays up here above the routes, over the landscape bars
                                // that come back with the app.
                                Platform.OS === "web" ? null : <WelcomeGalaxyEntryHost />
                              }
                            >
                              {Platform.OS === "web" ? (
                                <NavLayoutContent base={base} useDevtools={useDevtools} />
                              ) : (
                                // Signing out pictures the routes, so the welcome galaxy can launch out
                                // of the screen the user left.
                                <WelcomeGalaxyExitCapture>
                                  <NavLayoutContent base={base} useDevtools={useDevtools} />
                                </WelcomeGalaxyExitCapture>
                              )}
                            </LandscapeSafeAreaChrome>
                          </NavHanCoinDepositProvider>
                        </ToolbarSheetShadowProvider>
                      </PrimaryControlsStripToolbarHeightProvider>
                    </PrimaryControlsStripOpaqueChromeProvider>
                  </PrimaryControlsStripExpansionProvider>
                </PrimaryControlsStripTitleProvider>
              </PrimaryControlsStripBackProvider>
            </AuthSheetProvider>
          </FrostedBackdropProvider>
        </SpaceThemeScope>
      </NavContextProvider>
    </SessionExpiryBoundary>
  );
}

export function NavLayoutContent({
  base: _base,
  useDevtools,
}: {
  base: RoutePath;
  useDevtools?: () => void;
}) {
  (useDevtools ?? useNoopNavDevtools)();

  const pathname = usePathname();
  const segments = useSegments();
  // Auth and legal destinations never mount the app's tabs/sidebar. Their shared navigation
  // surface must release the splash too. Use route groups rather than a list of app URLs so new
  // app screens cannot accidentally reveal before their own chrome commits. The startup route
  // only redirects; revealing there would expose the temporary boot shell.
  const showsStandaloneReadyMarker =
    segments.includes("(nav)") && !segments.includes("(app)") && segments.at(-1) !== "startup";
  // Every `(app)` destination. App routes render no `PrimaryControlsStrip` — they mount
  // `NavAccountDrawerTrigger` instead. Derive from the route group (same pattern as the splash
  // marker above) so a new tab like cookbook/idioms cannot omit the drawer button and leave the
  // user stuck behind an empty bordered header. `/support` is a transparent-modal sibling of
  // `(app)`, not inside it, but still needs the same app-shell chrome.
  const isAppRoute =
    segments.includes("(app)") || pathname === "/support" || pathname.startsWith("/support/");
  const isAuthRoute = pathname.startsWith("/auth");
  const isBrowseRoute = pathname === "/browse" || pathname.startsWith("/browse/");
  // This component re-renders on EVERY navigation (it reads the pathname). Everything below it is
  // built by `NavLayoutChrome` from route booleans, so a bottom-tab switch — which changes
  // the pathname but none of them — no longer rebuilds and re-renders the account drawer tree.
  return (
    <ToolbarLeadingSlotAtLeftContext value={isBrowseRoute}>
      {/* Spans the scenes that float tabs and the drawer trigger, whose Admin panel rests above them. */}
      <PrimaryNavigationStripFloatingTabsProvider>
        <NavLayoutChrome
          isAppRoute={isAppRoute}
          isAuthRoute={isAuthRoute}
          isBrowseRoute={isBrowseRoute}
          isCharactersRoute={
            pathname === "/browse/characters" || pathname.startsWith("/browse/characters/")
          }
          isLearnRoute={pathname === "/learn" || pathname.startsWith("/learn/")}
          isSearchRoute={pathname === "/search" || pathname.startsWith("/search/")}
          isWelcomeRoute={pathname === "/auth"}
          showsStandaloneReadyMarker={showsStandaloneReadyMarker}
        />
      </PrimaryNavigationStripFloatingTabsProvider>
    </ToolbarLeadingSlotAtLeftContext>
  );
}

function NavLayoutChrome({
  isAppRoute,
  isAuthRoute,
  isBrowseRoute,
  isCharactersRoute,
  isLearnRoute,
  isSearchRoute,
  isWelcomeRoute,
  showsStandaloneReadyMarker,
}: {
  isAppRoute: boolean;
  isAuthRoute: boolean;
  isBrowseRoute: boolean;
  isCharactersRoute: boolean;
  isLearnRoute: boolean;
  /** Search's results grid rests 9pt under the pills, inside the band the long fade would cover. */
  isSearchRoute: boolean;
  /** The welcome galaxy draws its own backdrop (deep space, or paper) under the status bar. */
  isWelcomeRoute: boolean;
  showsStandaloneReadyMarker: boolean;
}) {
  const usesSidebarLayout = useUsesSidebarLayout() && !isAppRoute;
  const insets = useSafeAreaInsets();
  const standaloneReadyMarker = showsStandaloneReadyMarker ? <ChromeReadyMarker /> : null;
  // A focused full-page screen (the fresh-install welcome/auth gate) can suppress the entire
  // header so it renders edge-to-edge with no app chrome.
  const isPrimaryControlsStripHidden = useIsPrimaryControlsStripHidden();
  const hasOpaqueHeaderChrome = useHasPrimaryControlsStripOpaqueChrome();
  // How tall the FOCUSED screen's own opaque toolbar row is, straight from the screen rather
  // than assumed: `0` when it renders none, which drops the fade flush under the safe area. The
  // fade reaches full opacity at the row's bottom, feathering in through the 9pt gutter beneath
  // its controls so their glass shadows are not cut off. Its content-side dissolve starts at the
  // safe-area inset plus the row's own height. Computed, never measured — see the registry's docs for why a window
  // measurement of the row goes stale on a cold start and paints the band OVER the toolbar.
  const toolbarHeight = usePrimaryControlsStripToolbarHeight();
  const toolbarSurface = usePrimaryControlsStripToolbarSurface();
  // A glass bar floating over its page scroller (iOS 26) needs no fade: UIKit blurs the content
  // scrolling beneath it with the scroll edge effect of a system bar.
  const isToolbarFloating = usePrimaryControlsStripToolbarFloating();
  // A new learner's onboarding hands out the app one piece at a time (`lib/onboarding`): no More
  // menu until the tour has reached the cards, and nothing in the corners at all while the globe
  // covers the app (it waits in the drawer button's own slot). A page's own back control stays,
  // and so does Browse's More menu: its view and filter options shape the very lists the tour
  // introduces there. The drawer button is there from the moment the globe lands, so the learner
  // can always reach their account (and log out); the drawer itself lists only what the tour has
  // unlocked (`NavAccountDrawerContent`).
  const isOnboardingIntro = useIsOnboardingIntro();
  const isOnboardingTour = useIsOnboardingTour();
  // The onboarding opens on the Earth coming out of the welcome galaxy's dive, which waits for it:
  // its canvas is code-split, so it starts loading the moment the onboarding does.
  useEffect(() => {
    if (isOnboardingIntro) {
      preloadOnboardingGlobeCanvas();
    }
  }, [isOnboardingIntro]);
  const hasBackControl = usePrimaryControlsStripHasBackControl({
    appShellPlacement: "account-drawer",
  });
  // The app-wide corner switches: the trigger hides its buttons, and every toolbar row takes a
  // hidden button's lane, stretching to the window edge (the map's region tabs, Browse's tabs).
  const isDrawerButtonHidden = isOnboardingIntro;
  const isLeadingSlotHidden =
    isOnboardingIntro || (isOnboardingTour && !hasBackControl && !isBrowseRoute);
  const primaryControlsStripFadeTop = insets.top + (isAppRoute ? toolbarHeight : 0);
  // Routes whose content rests in the toolbar's own 9pt gutter get a short fade inside that
  // gutter instead of the long band below it, which would wash out their first row at rest.
  const hasGutterFade = isCharactersRoute || isLearnRoute || isSearchRoute;

  // Web is a dictionary-style site, not an app shell: one header (the account popover and
  // search) spans every `(nav)` destination — app, auth, and legal pages alike — and the `(app)`
  // group renders a plain `<NavRouteSlot />` beneath it (no bottom tabs). Browse and Search add
  // Browse's sidebar beside it; a focused full-page screen hides it with the header.
  if (Platform.OS === "web") {
    return (
      <View className="flex-1 bg-background" style={styles.root}>
        {isPrimaryControlsStripHidden ? null : <WebPrimaryControlsStrip />}
        <View className="flex-1" style={styles.root}>
          <WebBrowseSidebarLayout hidden={isPrimaryControlsStripHidden}>
            <NavRouteSlot />
          </WebBrowseSidebarLayout>
        </View>
        {/* The native twin lives in `PrimaryNavigationStrip`, which web never mounts, so a web hierarchy dump
            had no `actor-sync-diag` node and a stuck "Syncing data from cloud..." screen showed
            nothing about WHY. Every build, like native. */}
        {IS_SYNC_STATUS_SENTINEL_PUBLISHED ? <SyncStatusSentinel /> : null}
        {__DEV__ ? null : <WebActorOpenDiagnostics />}
        <HanCoinDepositOverlay />
      </View>
    );
  }

  // Keep one drawer above both app and auth routes. A sign-in push swaps the surface under
  // its scrim, rather than covering the app-owned drawer halfway through its closing slide.
  return (
    <ToolbarDrawerButtonHiddenContext value={isDrawerButtonHidden}>
      <ToolbarLeadingSlotHiddenContext value={isLeadingSlotHidden}>
        <NavAccountDrawer
          trigger={
            <>
              {/* Below the drawer button, so deposited coins drop in under its glass. */}
              <HanCoinDepositOverlay />
              {isAppRoute ? (
                // The corner controls sit on the focused top bar, so they follow its surface: over the
                // sky on a chrome bar, ordinary glass on a page-coloured one (morpheme panels).
                <SpaceSkySurfaceContext value={toolbarSurface === "chrome" && !isToolbarFloating}>
                  <NavAccountDrawerTrigger
                    landscapeLeadingRailWidth={PRIMARY_NAVIGATION_RAIL_WIDTH}
                    landscapeSceneRailWidth={
                      isLearnRoute ? PRIMARY_NAVIGATION_RAIL_WIDTH : undefined
                    }
                    leadingControl={
                      // One shell-owned slot always shows a page control or its loading fallback.
                      <PrimaryControlsStripLeadingHost />
                    }
                  />
                </SpaceSkySurfaceContext>
              ) : null}
              <NavEdgeGestures />
              <NavMapSheetShadow />
            </>
          }
        >
          <View className="flex-1 bg-background" style={styles.root}>
            {standaloneReadyMarker}
            {usesSidebarLayout ||
            isPrimaryControlsStripHidden ||
            isAppRoute ||
            isAuthRoute ? null : (
              <PrimaryControlsStrip />
            )}
            <View className="flex-1" style={styles.root}>
              <NavRouteSlot />
            </View>
            {!usesSidebarLayout &&
            isAppRoute &&
            !isPrimaryControlsStripHidden &&
            !hasOpaqueHeaderChrome &&
            !isToolbarFloating ? (
              // Start at the buttons' bottom edge and reach zero opacity 10px below it.
              <NavLayoutHeaderFade
                height={hasGutterFade ? 10 : undefined}
                overlap={!hasGutterFade && toolbarHeight > 0 ? 9 : 0}
                surface={toolbarSurface}
                top={primaryControlsStripFadeTop - (hasGutterFade && toolbarHeight > 0 ? 9 : 0)}
              />
            ) : null}
            {usesSidebarLayout || isAppRoute || isWelcomeRoute ? null : (
              <View
                className="absolute inset-x-0 top-0 z-50 bg-background"
                pointerEvents="none"
                style={{ height: insets.top }}
              />
            )}
            {__DEV__ && isAppRoute && !usesSidebarLayout ? <MissingTopBarDevCheck /> : null}
          </View>
        </NavAccountDrawer>
      </ToolbarLeadingSlotHiddenContext>
    </ToolbarDrawerButtonHiddenContext>
  );
}

/** Drawer presses hide only the fade; the outer shell and route slot keep their existing tree. */
function NavLayoutHeaderFade(props: ComponentProps<typeof PrimaryControlsStripFade>) {
  const { isOpen } = useNavAccountDrawer();
  const isHidden = useIsPrimaryControlsStripFadeHidden();
  return isOpen || isHidden ? null : <PrimaryControlsStripFade {...props} />;
}

/** How long a screen may go without a top bar (suspense fallbacks, transitions) before it is flagged. */
const MISSING_TOP_BAR_GRACE_MS = 3000;

/**
 * Development only. Every native `(app)` screen's top bar is `PrimaryControlsStripToolbar`; a
 * screen that draws its own heading instead strands the status band, the corner controls and the
 * header fade over its content. Flag it loudly rather than let it ship.
 */
function MissingTopBarDevCheck() {
  const hasToolbar = useHasPrimaryControlsStripToolbar();
  const pathname = usePathname();
  const [missingPathname, setMissingPathname] = useState<string | null>(null);
  // The support sheet is a modal sibling of `(app)`: the screen beneath it keeps its top bar.
  const isExempt = pathname === "/support" || pathname.startsWith("/support/");
  useEffect(() => {
    setMissingPathname(null);
    if (hasToolbar || isExempt) {
      return;
    }
    const timeout = setTimeout(setMissingPathname, MISSING_TOP_BAR_GRACE_MS, pathname);
    return () => clearTimeout(timeout);
  }, [hasToolbar, isExempt, pathname]);
  // Stays mounted and hides with style while every screen has its bar.
  return (
    <View
      className={
        missingPathname === null
          ? "hidden"
          : "absolute inset-x-3 bottom-32 z-50 rounded-xs bg-red-600 p-3"
      }
      pointerEvents="none"
      testID="missing-top-bar-dev-check"
    >
      <Text className="text-sm font-semibold text-white">
        {`No top bar on ${missingPathname ?? ""}: render PrimaryControlsStripToolbar (with a title if it has no controls).`}
      </Text>
    </View>
  );
}

/** Keep the app mounted underneath the route-owned support sheet. */
function NavRouteSlot() {
  const [isEdgeNavigationEnabled] = useDeviceSetting("isEdgeNavigationEnabled");
  if (Platform.OS === "web") {
    return <Slot />;
  }
  return (
    <Stack
      screenOptions={{
        animation: "none",
        animationMatchesGesture: false,
        fullScreenGestureEnabled: false,
        gestureEnabled: isEdgeNavigationEnabled,
        headerShown: false,
        keyboardHandlingEnabled: false,
      }}
    >
      <Stack.Screen
        name="support"
        options={{
          contentStyle: { backgroundColor: "transparent" },
          gestureEnabled: false,
          presentation: "transparentModal",
        }}
      />
    </Stack>
  );
}

/** The drawer scrim and its close controls must stay above page decoration. */
function NavMapSheetShadow() {
  const { drawerProgress, isOpen } = useNavAccountDrawer();
  return <ToolbarSheetShadow drawerProgress={drawerProgress} hidden={isOpen} />;
}
